import React, { Dispatch, SetStateAction, useMemo } from "react";
import { useStore } from "../../../store/store";
import fractionToInt from "../../../util/fractionToInt";
import {
  sliderPocketConfigOptions,
  sliderNonPocketConfigOptions,
} from "../../../initialValues/index";
import usePersistedState from "../../../hooks/usePersistedState";
import { SelectChangeEvent } from "@mui/material";
import { taggedSliderConfigs } from "../../../taggedImages/taggedSliderConfigs";

function useConfigSelector(
  sliderConfig: string,
  setSliderConfig: Dispatch<SetStateAction<string>>,
  submitPressed: boolean,
  editQuote: boolean
) {
  const addToQuote = useStore((state) => state.addToQuote);

  const sliderP = useStore((state) => state.quote.sliderPocket);
  const isPocketing = sliderP === "With Pocket";

  const chosenConfig = useStore((state) => state.quote.exactConfig);

  const handleErrMsgChange = useStore((state) => state.handleError);

  const unitSize = useStore((state) => state.quote.unitSize);
  const unitSizeFraction = useStore((state) => state.quote.unitSizeFraction);

  const sliderOverallWidth = fractionToInt(unitSize, unitSizeFraction);

  const [selectedSlider, setSelectedSlider] = usePersistedState(
    "selectedSlider",
    "",
    submitPressed,
    editQuote
  );

  const primaryRadio =
    chosenConfig?.includes("-") && chosenConfig?.split("-")[0].slice(-1) === "A"
      ? "Left"
      : chosenConfig?.includes("-") &&
        chosenConfig?.split("-")[1].slice(0, 1) === "A"
      ? "Right"
      : "";

  const borderColor = "#57d5f2";

  const handleChange = (event: SelectChangeEvent<string>) => {
    const val = event.target.value;
    setSliderConfig(val);

    if (val.includes("-")) {
      const findImg = taggedSliderConfigs.find(
        (option) => option.config === val
      );
      handleErrMsgChange("primaryDoorErr", "Select a Primary Door");

      addToQuote({
        exactConfigImg: findImg?.img1 || "",
        exactConfig: "",
      });
    } else {
      setSelectedSlider("");
      addToQuote({
        exactConfigImg: "",
        exactConfig: "",
      });
      handleErrMsgChange("primaryDoorErr", "");
    }
    handleErrMsgChange("configErr", "Select an Exact Configuration")
  };

  const handleRadio = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    if (value === "Left") {
      const config =
        sliderConfig === "PXXP"
          ? "PX-XP"
          : sliderConfig === "OXXO"
          ? "OX-XO"
          : sliderConfig;
      const sep = config.split("-");
      const separate = sep[0].split("");
      separate.splice(-1, 1, "A");

      const primary = separate.join("") + "-" + sep[1];
      addToQuote({
        exactConfig: primary,
        stilesAndRails: "",
        stileCategory: "",
        addScreen: "",
        screenPocket: "",
        screenColor: "",
      });
      handleErrMsgChange("primaryDoorErr", "");
    }
    if (value === "Right") {
      const config =
        sliderConfig === "PXXP"
          ? "PX-XP"
          : sliderConfig === "OXXO"
          ? "OX-XO"
          : sliderConfig;
      const sep = config.split("-");
      const separate = sep[1].split("");
      separate.splice(0, 1, "A");

      const primary = `${sep[0]}-${separate.join("")}`;
      addToQuote({
        exactConfig: primary,
        stilesAndRails: "",
        stileCategory: "",
        addScreen: "",
        screenPocket: "",
        screenColor: "",
      });
      handleErrMsgChange("primaryDoorErr", "");
    }
    handleErrMsgChange("configErr", "")
  };

  const handleImgClick = (config: string, side: string, image: string) => {
    if (config.includes("-")) return null;
    let chosen = "";

    if (side === "left") {
      chosen = config.includes("OR")
        ? config.split("OR")[0].trim()
        : `${config} Left Primary`;
    }
    if (side === "right") {
      chosen = config.includes("OR")
        ? config.split("OR")[1].trim()
        : `${config} Right Primary`;
    }
    setSelectedSlider(side);

    if (chosen === chosenConfig) return;

    addToQuote({
      exactConfig: chosen,
      exactConfigImg: image,
      stilesAndRails: "",
      stileCategory: "",
      addScreen: "",
      screenPocket: "",
      addSlidingScreen: "",
      screenType: "",
    });
    handleErrMsgChange("configErr", "")
  };

  const sliderConfigOptions = useMemo(
    () =>
      isPocketing
        ? sliderPocketConfigOptions(sliderOverallWidth)
        : sliderNonPocketConfigOptions(sliderOverallWidth),
    [isPocketing, sliderOverallWidth]
  );

  return {
    sliderConfigOptions,
    handleImgClick,
    handleRadio,
    handleChange,
    borderColor,
    primaryRadio,
    selectedSlider,
    isPocketing,
    chosenConfig,
  };
}

export default useConfigSelector;
